import { useMemo, HTMLProps } from "react";
import { Checkbox } from "react-migration/components/Checkbox/Checkbox";
import { Theme } from "react-migration/lib/theme/Theme";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { logEvent } from "react-migration/lib/util/logEvent";
import { dateIsoString } from "src/js/util/dates";
import Feature from "src/js/stores/user/Feature";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { CorePriceModes, PriceModes, ZooplaPriceModes } from "react-migration/domains/comparables/typings/PriceModes";
import { RecordType } from "react-migration/domains/comparables/typings/Record";
import { ZooplaComparableDTO } from "react-migration/lib/typings/Zoopla";
import { getHeadersFactory, getTransformer } from "react-migration/domains/comparables/util/BuildCSV";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import { Button } from "react-migration/components/Button/Button";
import { Tooltip } from "react-migration/components/Tooltip";

export interface ActionsBaseProps extends HTMLProps<HTMLDivElement> {
  loading: boolean;
  priceMode: PriceModes;
  transactions: RecordType[] | ZooplaComparableDTO[];
  incNoFloorArea: boolean;
  setIncNoFloorArea: (calcIncNoFloorArea: boolean) => void;
}

export interface ZooplaActionsProps extends Omit<ActionsBaseProps, "transactions" | "isZooplaData"> {
  transactions: ZooplaComparableDTO[];
  priceMode: ZooplaPriceModes;
}

export interface LandTechActionsProps extends Omit<ActionsBaseProps, "transactions" | "isZooplaData"> {
  transactions: RecordType[];
  priceMode: CorePriceModes;
}

export type ActionsProps = ActionsBaseProps | ZooplaActionsProps | LandTechActionsProps;
export function Actions(props: ZooplaActionsProps): JSX.Element;
export function Actions(props: LandTechActionsProps): JSX.Element;
export function Actions(props: ActionsBaseProps): JSX.Element;
export function Actions({
  loading,
  transactions,
  priceMode,
  incNoFloorArea,
  setIncNoFloorArea,
}: ActionsProps): JSX.Element {
  const { t } = useTranslation();
  const isZooplaData = isZoopla(priceMode);
  const unitPreference = getMeasurementSystemPreference();

  const csvLinkHeaders = useMemo(() => {
    return getHeadersFactory(priceMode)({ t, unitPreference });
  }, [t, unitPreference, priceMode]);

  const csvLinkData = useMemo(() => {
    const transformer = getTransformer(priceMode);
    const data = transformer({
      t,
      unitPreference,
      // @ts-expect-error transformer is polymorphic and was too much of headache to type correctly
      transactions,
    });

    return data;
  }, [t, transactions, unitPreference, priceMode]);

  const hasExportFeature = !isZooplaData || hasFeature(Feature.zooplaCompsCalculatorExport);
  const message = () => {
    if (!hasExportFeature) {
      return t("comparables.calculator.download.requires_license_upgrade");
    } else if (loading) {
      return t("comparables.calculator.results.messages.calculating");
    } else if (transactions.length === 0) {
      return t("comparables.calculator.results.messages.none");
    } else {
      return "";
    }
  };

  const CSVDownload = () => {
    logEvent("Comparables Download CSV", {
      includeNoFloorArea: incNoFloorArea,
      hometrack: isZooplaData,
      priceMode,
    });
  };

  return (
    <div className="flex flex-col items-center gap-2 pb-2" data-testid="calc-actions">
      <label className="flex! items-center text-sm ml-2">
        <Checkbox
          id="includeFloorArea"
          checked={incNoFloorArea}
          onCheckedChange={() => setIncNoFloorArea(!incNoFloorArea)}
        />
        <span className="pl-2 text-xs md:text-sm">{t("comparables.calculator.results.floorarea")}</span>
      </label>
      {csvLinkData.length && hasExportFeature ? (
        <CSVLink
          data={csvLinkData}
          headers={csvLinkHeaders}
          filename={`LandInsight Comparables Report (${dateIsoString(Date.now())}).csv`}
        >
          <Button onClick={() => CSVDownload()} leadIcon="lt-download-2-line">
            {t("comparables.calculator.download")}
          </Button>
        </CSVLink>
      ) : (
        <Tooltip.Wrapper>
          <Tooltip.Portal>
            <Tooltip.Content side="top" theme={Theme.Light}>
              <Tooltip.Description>{message()}</Tooltip.Description>
            </Tooltip.Content>
          </Tooltip.Portal>
          <Tooltip.Trigger>
            <Button leadIcon="lt-download-2-line" disabled data-testid="disabled-export-button">
              {t("comparables.calculator.download")}
            </Button>
          </Tooltip.Trigger>
        </Tooltip.Wrapper>
      )}
    </div>
  );
}
